/**
 * 列の構造変更(条件分岐・分岐質問の増減)を、全固定行のセルへ反映する。
 * 操作ごとに入口を分けてあり、updateLogicsInCells だけは1回の保存で追加・削除・維持が
 * 同時に起こるため、操作前の並びとの照合で何が残ったかを決める。
 *
 * 条件分岐は分岐質問の下にも置けるので、操作の対象は列とは限らない。対象は
 * 「列の位置 + 列からのパス」で特定し、固定行側も同じパスをたどる。
 *
 * 反映するのは構造(ノードの数と並び)だけ。内容は保存時に normalizeTableSections が
 * 列に揃えるので、編集中に同期しても誰も読まない。構造だけ揃えておくのは、
 * ノード数のソフトリミットを正しく数え、保存時に位置対応で id / uuid を引き継ぐため。
 */

import { TemplateNodeSchema } from '@ulysses-inc/harami_api_client'
import type { TemplateNodesDict } from 'src/exShared/types/types'
import { UUID } from 'src/exShared/util/uuid'
import { getCreateNodeId } from 'src/features/templateEdit/common/createNode'
import {
  collectSubtreeNodeIds,
  getLogicBranchQuestions,
  getQuestionLogicNodes,
  getRowCellQuestions,
  getTableColumnNodes,
  getTableFixedRowSectionNodes,
} from '../selector'
import { isTableSectionNode } from '../tableSection'
import type { SubtreePath } from './subtreePath'
import { findSubtreePath, resolveSubtreePath } from './subtreePath'

/**
 * 条件分岐の追加
 * 対象の質問の末尾に追加された条件分岐を、全固定行の対応する質問にも複製して追加する。
 * previousLogicCount は操作前の条件分岐の数。対象が表の列の中でなければ何もしない。
 */
export const appendLogicsToCells = (
  templateNodes: TemplateNodesDict,
  questionNodeId: number,
  previousLogicCount: number,
): TemplateNodesDict => {
  const location = locateInColumn(templateNodes, questionNodeId)
  if (!location) {
    return templateNodes
  }

  const appendedLogics = getQuestionLogicNodes(
    location.node,
    templateNodes,
  ).slice(previousLogicCount)

  // 条件分岐が増えていない場合は何もしない。
  if (appendedLogics.length === 0) {
    return templateNodes
  }

  let nextNodeId = getCreateNodeId(templateNodes)
  const allocateId = () => nextNodeId++
  const upsertedNodes: TemplateNodesDict = {}

  for (const cellQuestion of getCounterparts(templateNodes, location)) {
    const cellLogicIds = getQuestionLogicNodes(cellQuestion, templateNodes).map(
      logic => logic.id,
    )

    // 変更前の条件分岐の数と一致しない場合は、同期ロジックが壊れているので throw する。
    if (cellLogicIds.length !== previousLogicCount) {
      throwLogicCountMismatch(
        cellQuestion,
        cellLogicIds.length,
        previousLogicCount,
      )
    }

    const clonedIds = appendedLogics.map(logic => {
      const cloned = cloneStructure(logic, templateNodes, allocateId)
      Object.assign(upsertedNodes, cloned.nodes)
      return cloned.rootId
    })
    upsertedNodes[cellQuestion.id] = {
      ...cellQuestion,
      nodes: [...cellLogicIds, ...clonedIds],
    }
  }

  return applySyncResults(templateNodes, upsertedNodes, [])
}

/**
 * 条件分岐の削除
 * 全固定行の対応する質問から、削除された条件分岐と同じ位置のものをサブツリーごと削除する。
 * logicIndex は操作前の質問内での位置。
 */
export const removeLogicFromCells = (
  templateNodes: TemplateNodesDict,
  questionNodeId: number,
  logicIndex: number,
): TemplateNodesDict => {
  const location = requireInColumn(templateNodes, questionNodeId)
  const columnLogicCount = getQuestionLogicNodes(
    location.node,
    templateNodes,
  ).length

  const upsertedNodes: TemplateNodesDict = {}
  const removedIds: number[] = []

  for (const cellQuestion of getCounterparts(templateNodes, location)) {
    const cellLogicIds = getQuestionLogicNodes(cellQuestion, templateNodes).map(
      logic => logic.id,
    )

    // 変更前の条件分岐の数(削除後の列 + 1)と一致しない場合は、同期ロジックが壊れているので throw する。
    if (cellLogicIds.length !== columnLogicCount + 1) {
      throwLogicCountMismatch(
        cellQuestion,
        cellLogicIds.length,
        columnLogicCount + 1,
      )
    }

    // 消えた条件分岐と同じ位置のセル側条件分岐を、配下ごと削除する。
    removedIds.push(
      ...collectSubtreeNodeIds(cellLogicIds[logicIndex]!, templateNodes),
    )
    upsertedNodes[cellQuestion.id] = {
      ...cellQuestion,
      nodes: cellLogicIds.filter((_, index) => index !== logicIndex),
    }
  }

  return applySyncResults(templateNodes, upsertedNodes, removedIds)
}

/**
 * 分岐質問の追加
 * 列側条件分岐の末尾に追加された分岐質問を、全固定行の対応する条件分岐にも複製して追加する。
 * previousBranchCount は操作前の条件分岐の分岐質問の数。
 */
export const appendBranchToCells = (
  templateNodes: TemplateNodesDict,
  columnLogicId: number,
  previousBranchCount: number,
): TemplateNodesDict => {
  const location = requireInColumn(templateNodes, columnLogicId)
  const appendedBranches = getLogicBranchQuestions(
    location.node,
    templateNodes,
  ).slice(previousBranchCount)

  // 分岐質問が増えていない場合は何もしない。
  if (appendedBranches.length === 0) {
    return templateNodes
  }

  let nextNodeId = getCreateNodeId(templateNodes)
  const allocateId = () => nextNodeId++
  const upsertedNodes: TemplateNodesDict = {}

  for (const cellLogic of getCounterparts(templateNodes, location)) {
    const cellBranchIds = getLogicBranchQuestions(cellLogic, templateNodes).map(
      branch => branch.id,
    )

    // 変更前の分岐質問の数と一致しない場合は、同期ロジックが壊れているので throw する。
    if (cellBranchIds.length !== previousBranchCount) {
      throw new Error(
        `固定行の条件分岐(id: ${cellLogic.id})の分岐質問の数(${cellBranchIds.length})が操作前(${previousBranchCount})と一致しません`,
      )
    }

    const clonedIds = appendedBranches.map(branch => {
      const cloned = cloneStructure(branch, templateNodes, allocateId)
      Object.assign(upsertedNodes, cloned.nodes)
      return cloned.rootId
    })
    upsertedNodes[cellLogic.id] = {
      ...cellLogic,
      nodes: [...cellBranchIds, ...clonedIds],
    }
  }

  return applySyncResults(templateNodes, upsertedNodes, [])
}

/**
 * 分岐質問の削除。
 * 全固定行の対応する条件分岐から、削除された分岐質問と同じ位置のものを削除する。
 * branchIndex は操作前の条件分岐内での位置。
 */
export const removeBranchFromCells = (
  templateNodes: TemplateNodesDict,
  columnLogicId: number,
  branchIndex: number,
): TemplateNodesDict => {
  const location = requireInColumn(templateNodes, columnLogicId)
  const columnBranchCount = getLogicBranchQuestions(
    location.node,
    templateNodes,
  ).length

  const upsertedNodes: TemplateNodesDict = {}
  const removedIds: number[] = []

  for (const cellLogic of getCounterparts(templateNodes, location)) {
    const cellBranchIds = getLogicBranchQuestions(cellLogic, templateNodes).map(
      branch => branch.id,
    )

    // 変更前の分岐質問の数(削除後の列 + 1)と一致しない場合は、同期ロジックが壊れているので throw する。
    if (cellBranchIds.length !== columnBranchCount + 1) {
      throw new Error(
        `固定行の条件分岐(id: ${cellLogic.id})の分岐質問の数(${cellBranchIds.length})が操作前(${columnBranchCount + 1})と一致しません`,
      )
    }

    // 消えた分岐質問と同じ位置のセル側の分岐質問を削除する。
    removedIds.push(
      ...collectSubtreeNodeIds(cellBranchIds[branchIndex]!, templateNodes),
    )
    upsertedNodes[cellLogic.id] = {
      ...cellLogic,
      nodes: cellBranchIds.filter((_, index) => index !== branchIndex),
    }
  }

  return applySyncResults(templateNodes, upsertedNodes, removedIds)
}

/**
 * 条件分岐の一括更新(共用 UPDATE action)用。対象が表の列の中でなければ何もしない。
 *
 * 例: 条件分岐 A・B を持つ質問で、更新により B が消えて C が増えたとき
 *
 *   列:         [A, B] → [A, C]
 *   固定行セル:  [a, b] → [a, c]   ※ a, b は A, B に対応するセル側ノード
 *
 *   - 残った A → 対応ノード a をそのまま使う(id / uuid 不変)
 *   - 消えた B → b を配下ごと削除
 *   - 増えた C → 複製 c を同じ位置へ置く
 *
 * 何が残ったかは「操作前の並び(previousLogicIds)」と現在の並びの id 比較で決まる。
 */
export const updateLogicsInCells = (
  templateNodes: TemplateNodesDict,
  questionNodeId: number,
  previousLogicIds: readonly number[],
): TemplateNodesDict => {
  const location = locateInColumn(templateNodes, questionNodeId)
  if (!location) {
    return templateNodes
  }

  const currentLogicIds = getQuestionLogicNodes(
    location.node,
    templateNodes,
  ).map(logic => logic.id)
  if (
    previousLogicIds.length === currentLogicIds.length &&
    previousLogicIds.every((id, index) => id === currentLogicIds[index])
  ) {
    return templateNodes
  }

  let nextNodeId = getCreateNodeId(templateNodes)
  const allocateId = () => nextNodeId++
  const upsertedNodes: TemplateNodesDict = {}
  const removedIds: number[] = []

  for (const cellQuestion of getCounterparts(templateNodes, location)) {
    const cellLogicIds = getQuestionLogicNodes(cellQuestion, templateNodes).map(
      logic => logic.id,
    )

    // 変更前の条件分岐の数と一致しない場合は、同期ロジックが壊れているので throw する。
    if (cellLogicIds.length !== previousLogicIds.length) {
      throwLogicCountMismatch(
        cellQuestion,
        cellLogicIds.length,
        previousLogicIds.length,
      )
    }

    const nextCellLogicIds = currentLogicIds.map(currentLogicId => {
      const beforeIndex = previousLogicIds.indexOf(currentLogicId)
      if (beforeIndex >= 0) {
        // 例の A: 更新後も残っている → セル側の同位置ノードを使い回す。
        return cellLogicIds[beforeIndex]!
      }
      // 例の C: 更新で増えた → 列側を複製して同位置へ。
      const cloned = cloneStructure(
        templateNodes[currentLogicId]!,
        templateNodes,
        allocateId,
      )
      Object.assign(upsertedNodes, cloned.nodes)
      return cloned.rootId
    })

    // 例の B: 更新で消えた → 対応するセル側ノードをサブツリーごと削除対象に。
    removedIds.push(
      ...previousLogicIds.flatMap((previousLogicId, index) =>
        currentLogicIds.includes(previousLogicId)
          ? []
          : collectSubtreeNodeIds(cellLogicIds[index]!, templateNodes),
      ),
    )

    upsertedNodes[cellQuestion.id] = {
      ...cellQuestion,
      nodes: nextCellLogicIds,
    }
  }

  return applySyncResults(templateNodes, upsertedNodes, removedIds)
}

/** 操作対象のノードが、どの表のどの列の、列からどの位置にあるか。 */
type ColumnLocation = {
  tableNode: TemplateNodeSchema
  columnIndex: number
  /** 列から対象ノードまでのパス。 */
  path: SubtreePath
  /** 対象ノード(列側)。 */
  node: TemplateNodeSchema
}

/**
 * 対象ノードを含む表の列を探す。列そのものでも、その配下の条件分岐・分岐質問でもよい。
 * 表の列の中に無ければ undefined。
 */
const locateInColumn = (
  templateNodes: TemplateNodesDict,
  nodeId: number,
): ColumnLocation | undefined => {
  for (const tableNode of Object.values(templateNodes).filter(
    isTableSectionNode,
  )) {
    const columns = getTableColumnNodes(tableNode, templateNodes)
    for (const [columnIndex, columnNode] of columns.entries()) {
      const path = findSubtreePath(columnNode, nodeId, templateNodes)
      if (path) {
        return {
          tableNode,
          columnIndex,
          path,
          node: templateNodes[nodeId]!,
        }
      }
    }
  }
  return undefined
}

/** 表専用の操作用。対象が表の列の中に無いのは呼び出し側のバグなので throw する。 */
const requireInColumn = (
  templateNodes: TemplateNodesDict,
  nodeId: number,
): ColumnLocation => {
  const location = locateInColumn(templateNodes, nodeId)
  if (!location) {
    throw new Error(`対象ノード(id: ${nodeId})が表の列の中に見つかりません`)
  }
  return location
}

/**
 * 全固定行から、対象ノードに対応するノードを列挙する。
 * 固定行は列と同じ形を保つので、同じパスをたどれば対応先が決まる。
 */
const getCounterparts = (
  templateNodes: TemplateNodesDict,
  { tableNode, columnIndex, path }: ColumnLocation,
): TemplateNodeSchema[] =>
  getTableFixedRowSectionNodes(tableNode, templateNodes).map(row => {
    const cell = getRowCellQuestions(row, templateNodes)[columnIndex]
    if (!cell) {
      throw new Error(
        `固定行(id: ${row.id})に列位置 ${columnIndex} のセルがありません`,
      )
    }
    const counterpart = resolveSubtreePath(cell, path, templateNodes)
    if (!counterpart) {
      throw new Error(`固定行セル(id: ${cell.id})の構造が列と一致しません`)
    }
    return counterpart
  })

/**
 * 構造だけの複製。内容は保存時の同期が列で上書きするため、
 * 同一性の引き継ぎも参照の張り替えも行わない
 */
const cloneStructure = (
  sourceNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
  allocateId: () => number,
): { rootId: number; nodes: TemplateNodesDict } => {
  const nodes: TemplateNodesDict = {}
  const cloneNode = (source: TemplateNodeSchema): number => {
    const id = allocateId()
    nodes[id] = {
      ...structuredClone(source),
      id,
      uuid: UUID(),
      nodes: (source.nodes ?? []).map(childId =>
        cloneNode(templateNodes[childId]!),
      ),
    }
    return id
  }
  return { rootId: cloneNode(sourceNode), nodes }
}

/** 削除・追加・親の付け替えを templateNodes へ反映する。変化が無ければ同じ参照を返す。 */
const applySyncResults = (
  templateNodes: TemplateNodesDict,
  upsertedNodes: TemplateNodesDict,
  removedIds: readonly number[],
): TemplateNodesDict => {
  if (removedIds.length === 0 && Object.keys(upsertedNodes).length === 0) {
    return templateNodes
  }
  const removed = new Set(removedIds)
  const result: TemplateNodesDict = {}
  for (const [id, node] of Object.entries(templateNodes)) {
    if (!removed.has(Number(id))) {
      result[Number(id)] = node
    }
  }
  return { ...result, ...upsertedNodes }
}

const throwLogicCountMismatch = (
  cellNode: TemplateNodeSchema,
  cellLogicCount: number,
  expectedCount: number,
): never => {
  throw new Error(
    `固定行側の質問(id: ${cellNode.id})の条件分岐の数(${cellLogicCount})が操作前(${expectedCount})と一致しません`,
  )
}
